"use client";

import { Compass, Database, MapPin, User } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";
import { AiNavIcon } from "@/components/ai/entry/ai-nav-icon";
import { useLanguage } from "@/hooks/useLanguage";
import { getAiContextLabel } from "@/lib/ai/context";
import { writeAiEntryContext } from "@/lib/ai/client-settings";
import { discoverActivePatterns } from "@/lib/navigation/discover";
import { cn } from "@/lib/utils";

interface MobileNavItem {
	name: string;
	nameEn: string;
	icon: "plaza" | "data" | "ai" | "discover" | "me";
	href: string;
	activePatterns: string[];
	isAi?: boolean;
}

const navItems: MobileNavItem[] = [
	{
		name: "广场",
		nameEn: "Plaza",
		href: "/plaza",
		icon: "plaza",
		activePatterns: ["/plaza"],
	},
	{
		name: "数据",
		nameEn: "Data",
		href: "/data",
		icon: "data",
		activePatterns: ["/data", "/scan", "/address"],
	},
	{
		name: "Agent",
		nameEn: "Agent",
		href: "/ai",
		icon: "ai",
		activePatterns: ["/ai"],
		isAi: true,
	},
	{
		name: "发现",
		nameEn: "Discover",
		href: "/discover",
		icon: "discover",
		activePatterns: discoverActivePatterns,
	},
	{
		name: "我",
		nameEn: "Me",
		href: "/profile",
		icon: "me",
		activePatterns: ["/profile"],
	},
];

function renderIcon(icon: MobileNavItem["icon"]) {
	switch (icon) {
		case "plaza":
			return <MapPin className="h-5 w-5" />;
		case "data":
			return <Database className="h-5 w-5" />;
		case "discover":
			return <Compass className="h-5 w-5" />;
		case "me":
			return <User className="h-5 w-5" />;
		default:
			return null;
	}
}

export default function MobileNav() {
	const pathname = usePathname();
	const router = useRouter();
	const { language } = useLanguage();

	useEffect(() => {
		navItems.forEach((item) => {
			router.prefetch(item.href);
		});
	}, [router]);

	const isActive = (item: MobileNavItem) => {
		return item.activePatterns.some((pattern) => {
			if (pattern === "/") {
				return pathname === "/";
			}
			return pathname.startsWith(pattern);
		});
	};

	return (
		<div
			className="z-layer-mobile-nav fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 block md:hidden"
		>
			<div className="flex items-center justify-around px-2 py-2">
				{navItems.map((item) => {
					const active = isActive(item);
						const navContent = (
							<>
								<div
									className={cn(
										"flex h-5 items-center justify-center transition-transform duration-200",
										active ? "scale-110" : "scale-100",
									)}
								>
									{item.isAi ? (
										<AiNavIcon active={active} label="Agent" />
									) : (
										renderIcon(item.icon)
									)}
								</div>
								<span
									className={cn(
										"mt-1 truncate text-xs font-medium leading-none",
										active
											? "text-light-primary dark:text-dark-primary"
											: "text-gray-500 dark:text-gray-400",
									)}
								>
									{item.isAi ? "Agent" : language === "zh" ? item.name : item.nameEn}
								</span>
							</>
						);

					if (item.isAi) {
						return (
							<button
								key={item.name}
								type="button"
								onClick={() => {
									writeAiEntryContext({
										source: "mobile-nav",
										pathname,
										label: getAiContextLabel(pathname),
										timestamp: Date.now(),
									});
									router.push("/ai?source=mobile-nav");
								}}
								className={cn(
									"flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all duration-200 min-w-0 flex-1",
									active
										? "text-light-primary dark:text-dark-primary"
										: "text-gray-500 dark:text-gray-400 hover:text-light-primary dark:hover:text-dark-primary",
								)}
							>
								{navContent}
							</button>
						);
					}

					return (
						<Link
							key={item.name}
							href={item.href}
							className={cn(
								"flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all duration-200 min-w-0 flex-1",
								active
									? "text-light-primary dark:text-dark-primary"
									: "text-gray-500 dark:text-gray-400 hover:text-light-primary dark:hover:text-dark-primary",
							)}
						>
							{navContent}
						</Link>
					);
				})}
			</div>
		</div>
	);
}
